import { useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { BookOpen, CalendarBlank, PencilSimple, Eye } from '@phosphor-icons/react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import type { Mission, MissionSession, MissionStatus, Character, Member } from '@/types'
import { MissionViewerDialog } from './MissionViewerDialog'

interface MissionTimelineProps {
  tableId: string
  missions: Mission[]
  isGameMaster: boolean
  currentUserId: string
  characters?: Character[]
  members?: Member[]
  onSessionSummarySaved?: () => void
}

const STATUS_META: Record<MissionStatus, { label: string; variant: 'secondary' | 'default' | 'outline' }> = {
  rascunho: { label: 'Rascunho', variant: 'outline' },
  ativa: { label: 'Ativa', variant: 'default' },
  encerrada: { label: 'Encerrada', variant: 'secondary' },
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return iso
  }
}

function formatDuration(startIso: string, endIso?: string | null): string | null {
  if (!endIso) return 'em andamento'
  const mins = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000)
  if (mins < 1) return null
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m ? `${h}h${m.toString().padStart(2, '0')}` : `${h}h`
}

export function MissionTimeline({
  tableId,
  missions,
  isGameMaster,
  currentUserId,
  characters,
  members,
  onSessionSummarySaved,
}: MissionTimelineProps) {
  const [viewer, setViewer] = useState<{ missionId: string; title: string } | null>(null)
  const [hasPrologue, setHasPrologue] = useState(false)
  const [editingSession, setEditingSession] = useState<string | null>(null)
  const [summaryDraft, setSummaryDraft] = useState('')
  const [savingSummary, setSavingSummary] = useState(false)

  const visible = [...missions]
    .filter((m) => (isGameMaster ? true : m.status !== 'rascunho'))
    .sort((a, b) => a.order - b.order)

  // Detecta se há mensagens sem missão (Prólogo) — só faz sentido quando a mesa
  // já tem missões (mesa sem missões nem mostra a linha do tempo).
  useEffect(() => {
    if (missions.length === 0) {
      setHasPrologue(false)
      return
    }
    let cancelled = false
    apiService
      .getTableMessages(tableId, { limit: 1, missionId: 'none' })
      .then((res) => {
        if (!cancelled) setHasPrologue(res.messages.length > 0)
      })
      .catch(() => { /* silencioso — na dúvida, não mostra Prólogo */ })
    return () => {
      cancelled = true
    }
  }, [tableId, missions.length])

  const startEditSummary = (session: MissionSession) => {
    setEditingSession(session.id)
    setSummaryDraft(session.summary || '')
  }

  const saveSummary = async (mission: Mission, session: MissionSession) => {
    setSavingSummary(true)
    try {
      await apiService.updateMissionSession(tableId, mission.id, session.id, { summary: summaryDraft })
      toast.success('Resumo salvo')
      setEditingSession(null)
      onSessionSummarySaved?.()
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao salvar resumo')
    } finally {
      setSavingSummary(false)
    }
  }

  if (missions.length === 0) {
    // I1: mesa sem missões não mostra linha do tempo alguma.
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        A linha do tempo aparece quando a campanha tiver missões.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {hasPrologue && (
        <Card className="border-dashed">
          <CardContent className="flex items-center justify-between gap-3 p-4">
            <div className="flex items-center gap-2 text-sm">
              <BookOpen size={18} className="text-muted-foreground" />
              <span className="font-medium">Prólogo</span>
              <span className="text-muted-foreground">— mensagens anteriores às missões</span>
            </div>
            <Button size="sm" variant="outline" onClick={() => setViewer({ missionId: 'none', title: 'Prólogo' })}>
              <Eye size={14} className="mr-1" />
              Ver
            </Button>
          </CardContent>
        </Card>
      )}

      <ol className="relative flex flex-col gap-4 border-l-2 border-border pl-5">
        {visible.map((mission) => {
          const meta = STATUS_META[mission.status]
          const canView = mission.status !== 'rascunho'
          return (
            <li key={mission.id} className="relative">
              <span
                className={`absolute -left-[26px] top-1.5 h-3 w-3 rounded-full border-2 border-background ${
                  mission.status === 'ativa' ? 'bg-primary' : 'bg-muted-foreground'
                }`}
                aria-hidden
              />
              <Card className={mission.status === 'ativa' ? 'border-primary' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                          Missão {mission.order}
                        </span>
                        <Badge variant={meta.variant}>{meta.label}</Badge>
                      </div>
                      <h4 className="mt-1 truncate font-semibold">{mission.title}</h4>
                      {mission.description && (
                        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{mission.description}</p>
                      )}
                    </div>
                    {canView && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => setViewer({ missionId: mission.id, title: `Missão ${mission.order}: ${mission.title}` })}
                      >
                        <Eye size={14} className="mr-1" />
                        Ver
                      </Button>
                    )}
                  </div>

                  {mission.sessions.length > 0 && (
                    <ul className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
                      {mission.sessions.map((session) => {
                        const duration = formatDuration(session.started_at, session.ended_at)
                        return (
                          <li key={session.id} className="text-sm">
                            <div className="flex items-center gap-2">
                              <CalendarBlank size={14} className="shrink-0 text-muted-foreground" />
                              <span className="font-medium">{formatDate(session.started_at)}</span>
                              {duration && <span className="text-xs text-muted-foreground">· {duration}</span>}
                              {isGameMaster && editingSession !== session.id && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="ml-auto h-6 px-2 text-xs"
                                  onClick={() => startEditSummary(session)}
                                >
                                  <PencilSimple size={12} className="mr-1" />
                                  {session.summary ? 'Editar resumo' : 'Resumo'}
                                </Button>
                              )}
                            </div>
                            {editingSession === session.id ? (
                              <div className="mt-1 flex flex-col gap-2">
                                <Textarea
                                  value={summaryDraft}
                                  onChange={(e) => setSummaryDraft(e.target.value)}
                                  placeholder="Resumo do que aconteceu nesta sessão"
                                  rows={3}
                                />
                                <div className="flex gap-2">
                                  <Button size="sm" disabled={savingSummary} onClick={() => saveSummary(mission, session)}>
                                    Salvar
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => setEditingSession(null)}>
                                    Cancelar
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              session.summary && (
                                <p className="ml-6 mt-0.5 text-muted-foreground">{session.summary}</p>
                              )
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </li>
          )
        })}
      </ol>

      {viewer && (
        <MissionViewerDialog
          open={!!viewer}
          onOpenChange={(o) => !o && setViewer(null)}
          tableId={tableId}
          missionId={viewer.missionId}
          title={viewer.title}
          currentUserId={currentUserId}
          characters={characters}
          members={members}
        />
      )}
    </div>
  )
}
